import React from 'react';

import { canUserEditArtistMember, canUserAddArtistMember } from 'helpers/policies';
import { ArtistMembers } from 'components/Wizard/steps/ArtistMembers';
import { getMissingArtistMemberFields } from 'components/Wizard/steps/ArtistMembers/helpers';
import { WIZARD_STEP_IDS } from 'components/Wizard/constants';
import { StepCreatorParams, WizardStepConfig } from 'components/Wizard/types';

/**
 * Returns a configuration object for the Artist Members step in the Wizard, used to add
 * new member profiles to an artist or connect to existing profiles.
 */

export const createArtistMembersStep = ({
  artistProfile,
  user,
  context
}: StepCreatorParams): WizardStepConfig => {
  const artistMembers = artistProfile?.members || [];

  const memberMissingRequiredFields = artistMembers.some(member => {
    const missingFields = getMissingArtistMemberFields(member, context);
    return missingFields.length > 0;
  });

  const canUserAddMember = canUserAddArtistMember({
    user,
    artistProfile,
    memberMissingRequiredFields
  });
  const canEditOneMember =
    artistMembers.length === 0 ||
    artistMembers.some(member =>
      canUserEditArtistMember({ currentUser: user, artistProfile, member })
    );
  const viewOnly = !canUserAddMember && !canEditOneMember;

  const memberState = (() => {
    if (canUserAddMember && artistMembers.length === 0) return 'SETUP';
    if (viewOnly) return 'VIEW';
    if (memberMissingRequiredFields) return 'VERIFY';
    return 'EDIT';
  })();

  const plural = artistMembers.length ? 's' : '';

  const PARAMS_BY_STATE = {
    SETUP: {
      buttonText: 'Add Artist Member' + plural,
      subtitle: 'Provide basic contact info for each member of the artist.'
    },
    VERIFY: {
      buttonText: 'Verify Artist Member' + plural,
      subtitle: 'Provide basic contact info for each member of the artist.'
    },
    EDIT: {
      buttonText: `Edit Artist Member` + plural,
      subtitle: 'Provide basic contact info for each member of the artist.'
    },
    VIEW: {
      buttonText: 'View Artist Member' + plural,
      subtitle: 'Basic contact info for each member of the artist.'
    }
  };

  const params = PARAMS_BY_STATE[memberState];

  return {
    overviewRow: {
      title: 'Artist Members',
      subtitle: params.subtitle,
      // NOTE: by default, the button will navigate to the step url
      buttonText: params.buttonText
    },
    id: WIZARD_STEP_IDS.ARTIST_MEMBERS,
    path: `/artist-profile-editor/${context}/artist-members`,
    isRequired: true,
    isComplete: (!memberMissingRequiredFields && artistMembers.length >= 1) || viewOnly,
    render: wizardProps => (
      <ArtistMembers
        {...wizardProps}
        artistProfile={artistProfile}
        canUserAddMember={canUserAddMember}
        viewOnly={viewOnly}
      />
    )
  };
};
